import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import type { ErpErrorLogToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '~/lib/format-date';

interface ErpLogDetailDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	log: ErpErrorLogToken | null;
}

/** Read-only detail popup for a single ERP log row — shows the full (untruncated) SigNoz message + metadata. */
export function ErpLogDetailDialog({ open, onOpenChange, log }: ErpLogDetailDialogProps) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const copyMessage = async () => {
		if (!log?.Message) return;
		try {
			await globalThis.navigator?.clipboard?.writeText(log.Message);
			setCopied(true);
			globalThis.setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard unavailable (insecure context / denied) — no-op.
		}
	};

	const fields = [
		{ label: t('system.erpLogs.columns.date'), value: formatDateTime(log?.Timestamp) },
		{ label: t('system.erpLogs.columns.severity'), value: log?.Severity },
		{ label: t('system.erpLogs.columns.service'), value: log?.Service },
		{ label: t('system.erpLogs.columns.host'), value: log?.Host },
		{ label: t('system.erpLogs.columns.task'), value: log?.Task },
		{ label: t('system.erpLogs.columns.trace'), value: log?.TraceId }
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>{t('system.erpLogs.detail.title')}</DialogTitle>
					<DialogDescription>{[log?.Service, formatDateTime(log?.Timestamp)].filter(Boolean).join(' · ')}</DialogDescription>
				</DialogHeader>

				<dl className="grid grid-cols-[minmax(80px,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">
					{fields.map(f => (
						<div key={f.label} className="contents">
							<dt className="text-foreground-muted">{f.label}</dt>
							<dd className="min-w-0 break-words text-foreground">{f.value || '—'}</dd>
						</div>
					))}
				</dl>

				<div className="flex flex-col gap-1.5">
					<div className="flex items-center justify-between">
						<span className="text-xs font-medium text-foreground-muted">{t('system.erpLogs.columns.message')}</span>
						<Button variant="ghost" size="sm" onClick={copyMessage} disabled={!log?.Message}>
							{copied ? <Check size={14} /> : <Copy size={14} />}
							{copied ? t('system.erpLogs.detail.copied') : t('system.erpLogs.detail.copy')}
						</Button>
					</div>
					<pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-3 font-mono text-xs text-foreground">
						{log?.Message || '—'}
					</pre>
				</div>
			</DialogContent>
		</Dialog>
	);
}
